import React from 'react';
import { get, keyBy, groupBy, orderBy, fromPairs, chunk, max, } from 'lodash';
import { Link } from 'react-router-dom';
import { Button, Badge, } from 'reactstrap';
import { format as formatDate, addMonths, startOfMonth, } from 'date-fns';
import { toast } from 'react-toastify';
import { collection, query, where, getCountFromServer, } from 'firebase/firestore';

import firebase, { functions } from '../../firebase';
import { adminFields } from '../../shared/models/tenant';
import useCollectionSubscription from '../hooks/useCollectionSubscription';
import useQueryParams from '../hooks/useQueryParams';
import AdminPage from '../hocs/AdminPage';
import ModelFormModal from '../modals/ModelFormModal';
import EditButton from '../EditButton';
import QueryBoolean from '../QueryBoolean';
import ExportButton from '../ExportButton';
import ProgressButton from '../ProgressButton';

const db = firebase.firestore();
const tenantsRef = db.collection('tenants');
const banTenant = functions.httpsCallable('banTenant');
const toggleSalesDirect = functions.httpsCallable('toggleSalesDirect', { timeout: 540000 });
const backfillAmazonSales = functions.httpsCallable('backfillAmazonSales', { timeout: 540000 });

// 売上エクスポートで遡る月数
const SALES_EXPORT_MONTHS = 24;

const callToggleSalesDirectUntilDone = async (tenantId, salesDirect, toastId, cursor = null, totalDeleted = 0) => {
  const { data } = await toggleSalesDirect({ tenantId, salesDirect, cursor });
  if(data.done) return { totalDeleted };

  const nextTotal = totalDeleted + data.deletedCount;
  toast.update(toastId, { render: `直販切り替え中... ${nextTotal}件のステータスを削除` });
  return await callToggleSalesDirectUntilDone(tenantId, salesDirect, toastId, data.nextCursor, nextTotal);
};

export default AdminPage(function AdminTenants (props) {
  const { user } = props;
  const tenants = useCollectionSubscription(tenantsRef.orderBy('name'));
  const users = useCollectionSubscription(db.collection('users'));
  const usersGroupedByTenantId = groupBy(users, 'tenantId');
  const {
    onlyNeedToAccept: _onlyNeedToAccept = '0',
    onlySpApiAuthError: _onlySpApiAuthError = '0',
  } = useQueryParams();
  const onlyNeedToAccept = _onlyNeedToAccept === '1';
  const onlySpApiAuthError = _onlySpApiAuthError === '1';
  // 基本情報のエクスポート（売上カラムは含めない）
  const rowsForExport = async () => {
    const sortedTenants = orderBy(tenants, _ => _.createdAt?.toDate(), 'desc');
    const countOf = async q => (await getCountFromServer(q)).data().count;
    // 分母は取込候補の条件のうち Firestore で判定できる「新品・FBA」まで（在庫有無は SP-API が必要なため含めない）
    // テナント数ぶん一斉に投げると unavailable で失敗するため、少しずつ順番に集計する
    const productCountsByTenantId = fromPairs(await chunk(sortedTenants, 50).reduce(async (x, tenantsChunk) => [
      ...(await x),
      ...(await Promise.all(tenantsChunk.map(async ({ id, ref }) => [id, {
        registered: await countOf(query(collection(ref, 'products'))),
        loaded: await countOf(query(collection(ref, 'amazonProducts'), where('Offers.Offer.ItemCondition', '==', 'New'), where('fulfillmentChannelCode', '==', 'AMAZON_JP'))),
      }]))),
    ], Promise.resolve([])));
    return sortedTenants.map((tenant) => {
      const { id, name, fbaSettings, spApi, salesDirect = false, autoProductAdditionEnabled = false, chatworkApplied = false, businessType, sales, channels, createdAt, } = tenant;
      const users = usersGroupedByTenantId[id];
      const productCounts = productCountsByTenantId[id];
      return {
        id,
        createdAt: createdAt && formatDate(createdAt.toDate(), 'yyyy/MM/dd HH:mm:ss'),
        name,
        userEmail: users?.map(_ => _.email).join(', '),
        spApi: spApi?.accessToken != null ? '認証済み' : '',
        salesDirect: salesDirect ? '直販する' : '',
        autoProductAddition: autoProductAdditionEnabled ? 'ON' : 'OFF',
        // 分子には新品・FBA以外（自社出荷に切り替えたもの等）も含まれ分母を超えうるため、最大100%に丸める
        productRegistrationRate: `${productCounts.registered}/${max([productCounts.registered, productCounts.loaded])}`,
        chatworkApplied: chatworkApplied ? 'ON' : 'OFF',
        businessType,
        sales,
        channels,
      };
    });
  };

  // 売上エクスポート（基本情報＋h+売上・Amazon売上を過去24ヶ月分）
  const rowsForSalesExport = async () => {
    const now = new Date();
    // 当月を含めて過去24ヶ月（古い順）
    const months = [...Array(SALES_EXPORT_MONTHS).keys()].map(_ => startOfMonth(addMonths(now, -(SALES_EXPORT_MONTHS - 1 - _))));
    const monthStart = months[0];
    const monthEnd = startOfMonth(addMonths(now, 1));
    const sortedTenants = orderBy(tenants, _ => _.createdAt?.toDate(), 'desc');
    // 全テナントの売上を collection group クエリで一括取得（テナント数ぶんのクエリを避ける）
    const toAmountByTenantMonth = (snap) => groupBy(
      snap.docs.map(_ => ({ tenantId: _.ref.parent.parent.id, month: formatDate(_.data().targetMonth.toDate(), 'yyyy/MM'), amount: _.data().amount || 0 })),
      'tenantId',
    );
    const [salesSnap, amazonSalesSnap] = await Promise.all([
      db.collectionGroup('sales').where('targetMonth', '>=', monthStart).where('targetMonth', '<', monthEnd).get(),
      db.collectionGroup('amazonSales').where('targetMonth', '>=', monthStart).where('targetMonth', '<', monthEnd).get(),
    ]);
    const salesGroupedByTenantId = toAmountByTenantMonth(salesSnap);
    const amazonSalesGroupedByTenantId = toAmountByTenantMonth(amazonSalesSnap);
    return sortedTenants.map((tenant) => {
      const { id, name, fbaSettings, spApi, salesDirect = false, autoProductAdditionEnabled = false, businessType, sales, channels, createdAt, } = tenant;
      const users = usersGroupedByTenantId[id];
      const hanroAmountByMonth = keyBy(salesGroupedByTenantId[id] ?? [], 'month');
      const amazonAmountByMonth = keyBy(amazonSalesGroupedByTenantId[id] ?? [], 'month');
      // h+売上を24ヶ月ぶん並べたあとに、Amazon売上を24ヶ月ぶん並べる
      const salesColumns = fromPairs([
        ...months.map(month => [`h+売上 ${formatDate(month, 'yyyy/MM')}`, hanroAmountByMonth[formatDate(month, 'yyyy/MM')]?.amount ?? 0]),
        ...months.map(month => [`Amazon売上 ${formatDate(month, 'yyyy/MM')}`, amazonAmountByMonth[formatDate(month, 'yyyy/MM')]?.amount ?? 0]),
      ]);
      return {
        id,
        createdAt: createdAt && formatDate(createdAt.toDate(), 'yyyy/MM/dd HH:mm:ss'),
        name,
        userEmail: users?.map(_ => _.email).join(', '),
        spApi: spApi?.accessToken != null ? '認証済み' : '',
        salesDirect: salesDirect ? '直販する' : '',
        autoProductAddition: autoProductAdditionEnabled ? 'ON' : 'OFF',
        businessType,
        sales,
        channels,
        ...salesColumns,
      };
    });
  };

  // 手動集計ボタンは現在非表示。再表示時に使うため定義は残す
  // eslint-disable-next-line no-unused-vars
  const onClickBackfillAmazonSales = async () => {
    if(!window.confirm(`全テナントのAmazon売上（過去${SALES_EXPORT_MONTHS}ヶ月）を集計します。SP-APIを多数呼び出すため数分かかることがあります。よろしいですか？`)) return;

    const toastId = toast.info('Amazon売上を集計中...', { autoClose: false });
    try {
      const { data } = await backfillAmazonSales({ months: SALES_EXPORT_MONTHS });
      toast.update(toastId, { render: `Amazon売上の集計が完了しました（集計${data.computed}件 / スキップ${data.skipped}件 / 失敗${data.failed}件）`, type: 'success', autoClose: 8000 });
    } catch(e) {
      console.error(e);
      toast.update(toastId, { render: 'Amazon売上の集計に失敗しました', type: 'error', autoClose: false });
    }
  };

  return (
    <div className="admin-tenants container-fluid">
      <div className="p-4 bg-white my-4">
        <div className="d-flex justify-content-center mb-3">
          <h4>テナント管理</h4>
        </div>
        <div className="mt-3 d-flex align-items-end justify-content-between flex-wrap mb-1">
          <div className="d-flex gap-2">
            <QueryBoolean paramName="onlyNeedToAccept" label="承認が必要なもののみ" defaultValue={'0'} />
            <QueryBoolean paramName="onlySpApiAuthError" label="SP API接続エラーのもののみ" defaultValue={'0'} />
          </div>
          <div className="d-flex gap-2">
            {/* Amazon売上は定期実行で集計されるため手動ボタンは非表示。必要時にコメントを外して使う */}
            {/* <ProgressButton color="primary" label="Amazon売上を集計" process={onClickBackfillAmazonSales} /> */}
            <ExportButton fileName="tenants.csv" rows={rowsForExport} />
            <ExportButton fileName="tenant-sales.csv" label="売上エクスポート" rows={rowsForSalesExport} />
          </div>
        </div>
        <div className="overflow-scroll">
          <table className="table sticky-table">
            <thead className="thead-light text-center">
              <tr className="text-nowrap">
                <th>
                  ID / テナントキー
                </th>
                <th>会社名</th>
                <th>ユーザーメールアドレス</th>
                <th>SP API認証</th>
                <th>FBA設定</th>
                <th>直販する</th>
                <th>ハンロプラスロジ</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {
                tenants.map((tenant) => {
                  const { id, } = tenant;
                  const users = usersGroupedByTenantId[id];

                  return (
                    <TenantRow key={id} tenant={tenant} users={users} onlyNeedToAccept={onlyNeedToAccept} onlySpApiAuthError={onlySpApiAuthError} user={user} />
                  );
                })
              }
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
});

function TenantRow(props) {
  const { user, tenant, users, onlyNeedToAccept, onlySpApiAuthError, } = props;
  const { id, keyForMall, name, fbaSettings, spApi, salesDirect = false, enablesLogi = false, premium = false, chatworkApplied = false, logispId, isBlack = false, isInactive = false, } = tenant;
  const onClickAccept = async (accepted, type) => {
    if(!window.confirm(`本当に${accepted ? '承認' : '不承認'}済みにしますか？`)) return;

    await tenant.ref.update({ [type]: { ...tenant[type], accepted } });
  };
  const needsToAccept = ['fbaSettings'].some((type) => {
    return tenant[type] != null && tenant[type]?.accepted == null;
  });
  const changeTenant = async () => {
    if(!window.confirm(`${name}のユーザーになります。よろしいですか？`)) return;

    await user.ref.update({ tenantId: id });
  };
  const onClickBan = async () => {
    if(!window.confirm(`本当に「${name}」をブラックリストに追加しますか？\nテナントが非アクティブ化され、紐づくユーザーアカウントが削除されます。`)) return;

    try {
      await banTenant({ tenantId: id });
      alert('ブラックリストに追加しました');
    } catch(e) {
      alert('失敗しました');
      console.error(e);
    }
  };
  const onClickToggleSalesDirect = async () => {
    const next = !salesDirect;
    if(!window.confirm(`「${name}」の直販を${next ? 'ON' : 'OFF'}に切り替えます。\nこのテナントの出品ステータス情報がすべて削除されます。よろしいですか？`)) return;

    const toastId = toast.info('直販切り替え中...', { autoClose: false });
    try {
      const result = await callToggleSalesDirectUntilDone(id, next, toastId);
      toast.update(toastId, { render: `直販を${next ? 'ON' : 'OFF'}に切り替えました（${result.totalDeleted}件のステータスを削除）`, type: 'success', autoClose: 5000 });
    } catch(e) {
      console.error({ e });
      // firebase compat の callable は code が 'functions/failed-precondition' のようにプレフィックス付きになるため末尾一致で判定
      const message = e?.code?.endsWith('failed-precondition') ? e.message : '直販切り替えに失敗しました';
      // react-toastify v5 の toast.update は render に JSX を渡すと反映されないため文字列で渡す（改行は .Toastify__toast-body の white-space: pre-line で表示される）
      toast.update(toastId, { render: message, type: 'error', autoClose: false });
    }
  };

  return (!onlyNeedToAccept || needsToAccept) && (!onlySpApiAuthError || tenant.spApiAuthErrorMessage != null) && (
    <tr>
      <td>
        <Link to={`/admin/tenants/${id}`}>
          {id}
        </Link>
        <div>
          {keyForMall}
        </div>
        {
          user.dev && (
            <a className="d-block" href={`https://console.firebase.google.com/u/0/project/hanro-plus/firestore/data/~2Ftenants~2F${tenant.id}`} target="_blank">
              F
            </a>
          )
        }
      </td>
      <td>
        {name}
        {isInactive && <Badge color="danger" className="ml-2">退会</Badge>}
        {premium && <Badge color="warning" className="ml-2">プレミアムショップ</Badge>}
        {chatworkApplied && <Badge color="info" className="ml-2">ChatWork申請済み</Badge>}
      </td>
      <td>
        {users?.map(_ => _.email).join(', ')}
      </td>
      <td>
        <div>{spApi?.accessToken != null && '認証済み'}</div>
        {spApi?.accessToken != null && tenant.spApiAuthStatus && <div className="mt-1 small">接続状況: {tenant.spApiAuthStatus}</div>}
        {tenant.spApiAuthStatus === 'error' && tenant.spApiAuthErrorMessage && <div className="mt-1 alert alert-danger small">{tenant.spApiAuthErrorMessage}</div>}
      </td>
      {
        ['fbaSettings'].map((type) => {
          return (
            <td key={type}>
              {
                tenant[type] != null && (
                  <div>
                    <a href={tenant[type].downloadUrl} target="_blank">
                      <img src={tenant[type].downloadUrl} style={{ maxWidth: 50, maxHeight: 30, }} />
                    </a>
                    {
                      tenant[type].accepted == null ? (
                        <div className="mt-2">
                          <Button color="success" onClick={onClickAccept.bind(null, true, type)}>
                            承認
                          </Button>
                          <Button color="warning" className="ml-2" onClick={onClickAccept.bind(null, false, type)}>
                            不承認
                          </Button>
                        </div>
                      ) : (
                        <span className="ml-2 text-muted small">{tenant[type].accepted ? '承認済み' : '不承認済み'}</span>
                      )
                    }
                  </div>
                )
              }
            </td>
          );
        })
      }
      <td className="text-center text-nowrap">
        <div>{salesDirect ? '直販する' : '直販しない'}</div>
        <ProgressButton
          className="mt-1"
          size="sm"
          color={salesDirect ? 'secondary' : 'primary'}
          label={salesDirect ? '直販OFFに切り替え' : '直販ONに切り替え'}
          process={onClickToggleSalesDirect}
        />
      </td>
      <td>
        <div>{logispId}</div>
      </td>
      <td className="text-nowrap text-right">
        <EditButton itemRef={tenant.ref} FormModal={ModelFormModal} formProps={{ title: '編集', fields: adminFields() }} />
        <ProgressButton className="ml-1" label="このテナントのユーザーになる" process={changeTenant} disabled={user.tenantId === id} />
        <ProgressButton className="ml-1" color="danger" label="ブラックリスト" process={onClickBan} disabled={isBlack} />
      </td>
    </tr>
  );
}
